import { useEffect, useRef, useState } from 'react';
import { Layout } from '@/components/Layout';
import { centralApi } from '@/services/centralApi';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { QrCode, RefreshCw, LogOut, Smartphone, ShieldCheck, ShieldAlert } from 'lucide-react';

const StatusDot = ({ color = 'gray' }) => {
  const cls = {
    green: 'bg-emerald-500',
    amber: 'bg-amber-500 animate-pulse',
    red: 'bg-red-500',
    gray: 'bg-slate-400',
  }[color];
  return <span className={`inline-block h-2 w-2 rounded-full ${cls}`} />;
};

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString('pt-BR') : '—');

export default function CentralConfigWhatsApp() {
  const [cfg, setCfg] = useState(null);
  const [baileys, setBaileys] = useState(null);
  const [testing, setTesting] = useState(false);
  const [busy, setBusy] = useState(false);
  const pollRef = useRef(null);

  const provider = cfg?.whatsapp_provider || 'baileys';

  const loadAll = async () => {
    try {
      const s = await centralApi.getWaSettings();
      setCfg(s);
      if ((s.whatsapp_provider || 'baileys') === 'baileys') {
        const b = await centralApi.getBaileysStatus();
        setBaileys(b);
      }
    } catch (e) {
      console.error(e);
    }
  };

  // Polling do status Baileys a cada 3s (só enquanto na aba Baileys)
  useEffect(() => {
    loadAll();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  useEffect(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    if (provider !== 'baileys') return;
    pollRef.current = setInterval(async () => {
      try {
        const b = await centralApi.getBaileysStatus();
        setBaileys(b);
      } catch (_e) { /* ignora rede transiente */ }
    }, 3000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [provider]);

  const changeProvider = async (novo) => {
    try {
      const upd = await centralApi.updateWaSettings({ whatsapp_provider: novo });
      setCfg(upd);
      toast.success(`Provider ativo: ${novo === 'baileys' ? 'Baileys (QR Code)' : 'Meta Cloud API'}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao alterar provider');
    }
  };

  const saveMeta = async () => {
    try {
      const updated = await centralApi.updateWaSettings({
        business_name: cfg.business_name,
        display_number: cfg.display_number,
        waba_id: cfg.waba_id,
        phone_number_id: cfg.phone_number_id,
        webhook_url: cfg.webhook_url,
      });
      setCfg(updated);
      toast.success('Configurações salvas');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao salvar');
    }
  };

  const testarMeta = async () => {
    setTesting(true);
    try {
      const r = await centralApi.testWaConnection();
      if (r.ok) toast.success('Conexão OK');
      else toast.warning(r.detail || 'Falha na conexão');
    } finally { setTesting(false); }
  };

  const gerarNovoQr = async () => {
    setBusy(true);
    try {
      await centralApi.resetBaileys();
      toast.success('Gerando novo QR Code…');
      // deixa o polling atualizar
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao gerar QR');
    } finally { setBusy(false); }
  };

  const desconectar = async () => {
    if (!window.confirm('Desconectar o WhatsApp? Você precisará escanear um novo QR Code para reconectar.')) return;
    setBusy(true);
    try {
      await centralApi.disconnectBaileys();
      toast.success('WhatsApp desconectado');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao desconectar');
    } finally { setBusy(false); }
  };

  if (!cfg) return <Layout><div className="text-sm text-muted-foreground p-6">Carregando…</div></Layout>;

  const bailStatus = baileys?.status || 'disconnected';
  const bailColor = bailStatus === 'connected' ? 'green' : bailStatus === 'qr_pending' ? 'amber' : bailStatus === 'connecting' ? 'amber' : 'red';
  const bailLabel = {
    connected: 'Conectado',
    qr_pending: 'Aguardando QR',
    connecting: 'Conectando',
    disconnected: 'Desconectado',
  }[bailStatus] || bailStatus;

  return (
    <Layout>
      <div className="space-y-4" data-testid="central-config-whatsapp">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold">Conexão WhatsApp</h1>
          <p className="text-sm text-muted-foreground">Escolha o provider e conecte o WhatsApp da Serraria Guedes</p>
        </div>

        {/* Provider selector */}
        <Card className="border-border">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-amber-600" />
              Provider ativo
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Tabs value={provider} onValueChange={changeProvider} className="w-full">
              <TabsList className="grid w-full max-w-md grid-cols-2" data-testid="provider-tabs">
                <TabsTrigger value="baileys" data-testid="provider-tab-baileys">
                  <Smartphone className="h-4 w-4 mr-1" /> Baileys (QR Code)
                </TabsTrigger>
                <TabsTrigger value="meta" data-testid="provider-tab-meta">
                  <ShieldAlert className="h-4 w-4 mr-1" /> Meta Cloud API
                </TabsTrigger>
              </TabsList>
              <TabsContent value="baileys" className="mt-4">
                <p className="text-xs text-muted-foreground">
                  Conecta um número via QR Code (WhatsApp Business no celular → Dispositivos conectados). Não requer token da Meta.
                </p>
              </TabsContent>
              <TabsContent value="meta" className="mt-4">
                <p className="text-xs text-muted-foreground">
                  Integração oficial via Meta Cloud API. Exige WABA ID, Phone Number ID e Access Token permanente da Meta.
                </p>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        {/* ============ PROVIDER: BAILEYS ============ */}
        {provider === 'baileys' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="baileys-panel">
            <Card className="border-border">
              <CardHeader><CardTitle className="text-base">Status da conexão</CardTitle></CardHeader>
              <CardContent className="space-y-3 text-sm">
                <div className="flex items-center justify-between">
                  <span>WhatsApp</span>
                  <Badge variant="outline" data-testid="baileys-status-badge">
                    <StatusDot color={bailColor} /> <span className="ml-1">{bailLabel}</span>
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span>Serviço Node</span>
                  <Badge variant="outline">
                    <StatusDot color={baileys?.node_online === false ? 'red' : 'green'} />
                    <span className="ml-1">{baileys?.node_online === false ? 'Offline' : 'Online'}</span>
                  </Badge>
                </div>
                <div className="pt-3 border-t space-y-1">
                  <p><span className="text-muted-foreground">Número conectado:</span>{' '}
                    <span data-testid="baileys-number">{baileys?.connected_number ? `+${baileys.connected_number}` : '—'}</span>
                  </p>
                  <p><span className="text-muted-foreground">Nome da conta:</span> {baileys?.connected_name || '—'}</p>
                  <p><span className="text-muted-foreground">Última conexão:</span> {fmtDate(baileys?.last_connected_at)}</p>
                  <p><span className="text-muted-foreground">Última recebida:</span> {fmtDate(baileys?.last_incoming_at)}</p>
                  <p><span className="text-muted-foreground">Última enviada:</span> {fmtDate(baileys?.last_outgoing_at)}</p>
                  {baileys?.last_error && (
                    <p className="text-red-600 text-xs mt-2 bg-red-50 dark:bg-red-950/20 rounded px-2 py-1">
                      {baileys.last_error}
                    </p>
                  )}
                </div>
                <div className="flex gap-2 pt-3">
                  <Button onClick={gerarNovoQr} disabled={busy} size="sm" data-testid="btn-gerar-qr">
                    <RefreshCw className={`h-4 w-4 mr-1 ${busy ? 'animate-spin' : ''}`} /> Gerar novo QR
                  </Button>
                  {bailStatus === 'connected' && (
                    <Button onClick={desconectar} disabled={busy} variant="outline" size="sm" data-testid="btn-desconectar">
                      <LogOut className="h-4 w-4 mr-1" /> Desconectar
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card className="border-border">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <QrCode className="h-4 w-4 text-amber-600" />
                  QR Code de vinculação
                </CardTitle>
              </CardHeader>
              <CardContent>
                {bailStatus === 'connected' ? (
                  <div className="flex flex-col items-center justify-center py-8 text-center">
                    <div className="h-16 w-16 rounded-full bg-emerald-100 dark:bg-emerald-950/30 flex items-center justify-center mb-3">
                      <ShieldCheck className="h-8 w-8 text-emerald-600" />
                    </div>
                    <p className="font-semibold">WhatsApp conectado</p>
                    <p className="text-xs text-muted-foreground mt-1">Não é necessário escanear QR novamente.</p>
                  </div>
                ) : baileys?.qr_data_url ? (
                  <div className="flex flex-col items-center">
                    <img
                      src={baileys.qr_data_url}
                      alt="QR Code para conectar WhatsApp"
                      className="w-64 h-64 border rounded-md p-2 bg-white"
                      data-testid="baileys-qr-image"
                    />
                    <ol className="mt-4 text-xs text-muted-foreground space-y-1 list-decimal pl-4 max-w-sm">
                      <li>Abra o <strong>WhatsApp Business</strong> no celular</li>
                      <li>Toque em <strong>Configurações</strong> → <strong>Dispositivos conectados</strong></li>
                      <li>Toque em <strong>Conectar dispositivo</strong> e aponte a câmera para o QR acima</li>
                    </ol>
                  </div>
                ) : (
                  <div className="flex flex-col items-center py-8 text-center">
                    <div className="h-12 w-12 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center mb-3 animate-pulse">
                      <QrCode className="h-6 w-6 text-slate-400" />
                    </div>
                    <p className="text-sm text-muted-foreground">Aguardando QR Code do serviço…</p>
                    <p className="text-[11px] text-muted-foreground mt-1">Se demorar mais de 30s, clique em "Gerar novo QR".</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* ============ PROVIDER: META ============ */}
        {provider === 'meta' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="meta-panel">
            <Card className="border-border">
              <CardHeader><CardTitle className="text-base">Status</CardTitle></CardHeader>
              <CardContent className="space-y-2 text-sm">
                <div className="flex items-center justify-between">
                  <span>WhatsApp</span>
                  <Badge variant="outline"><StatusDot color={cfg.connected ? 'green' : 'red'} /> <span className="ml-1">{cfg.connected ? 'Conectado' : 'Não conectado'}</span></Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span>Webhook</span>
                  <Badge variant="outline"><StatusDot color={cfg.webhook_status === 'ok' ? 'green' : 'gray'} /> <span className="ml-1">{cfg.webhook_status === 'ok' ? 'Funcionando' : 'Aguardando'}</span></Badge>
                </div>
                <div className="pt-2 border-t space-y-1">
                  <p><span className="text-muted-foreground">Nome comercial:</span> {cfg.business_name || '—'}</p>
                  <p><span className="text-muted-foreground">Número exibido:</span> {cfg.display_number || '—'}</p>
                  <p><span className="text-muted-foreground">Última recebida:</span> {fmtDate(cfg.last_incoming_at)}</p>
                  <p><span className="text-muted-foreground">Última enviada:</span> {fmtDate(cfg.last_outgoing_at)}</p>
                  {cfg.last_connection_error && (
                    <p className="text-red-600 text-xs mt-2 bg-red-50 dark:bg-red-950/20 rounded px-2 py-1">{cfg.last_connection_error}</p>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card className="border-border lg:col-span-2">
              <CardHeader><CardTitle className="text-base">Identificadores</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <label className="text-xs font-medium">Nome comercial</label>
                  <Input value={cfg.business_name || ''} onChange={(e) => setCfg({ ...cfg, business_name: e.target.value })} />
                </div>
                <div>
                  <label className="text-xs font-medium">Número exibido</label>
                  <Input placeholder="+55 83 99999-9999" value={cfg.display_number || ''} onChange={(e) => setCfg({ ...cfg, display_number: e.target.value })} />
                </div>
                <div>
                  <label className="text-xs font-medium">WABA ID</label>
                  <Input value={cfg.waba_id || ''} onChange={(e) => setCfg({ ...cfg, waba_id: e.target.value })} />
                </div>
                <div>
                  <label className="text-xs font-medium">Phone Number ID</label>
                  <Input value={cfg.phone_number_id || ''} onChange={(e) => setCfg({ ...cfg, phone_number_id: e.target.value })} />
                </div>
                <div>
                  <label className="text-xs font-medium">Webhook URL</label>
                  <Input value={cfg.webhook_url || ''} onChange={(e) => setCfg({ ...cfg, webhook_url: e.target.value })} />
                  <p className="text-[11px] text-muted-foreground mt-1">
                    Tokens e chaves nunca são exibidos aqui. Configure-os nas variáveis de ambiente do backend.
                  </p>
                </div>
                <div className="flex gap-2 pt-2">
                  <Button onClick={saveMeta}>Salvar</Button>
                  <Button variant="outline" onClick={testarMeta} disabled={testing} data-testid="btn-test-wa">
                    {testing ? 'Testando…' : 'Testar conexão'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </Layout>
  );
}
